<template>
    <div>
        <!-- Experiência v2: shell próprio em todo o sistema autenticado -->
        <template v-if="activeShellIsV2">
            <DashboardV2AppShell>
                <router-view v-if="routeContentReady" />
            </DashboardV2AppShell>
            <NotificationToast />
            <LegalGraceBanner />
        </template>

        <template v-else>
            <Header v-if="!layoutState.isAuth"></Header>

            <div class="main-container" id="container" :class="containerClasses">
                <div v-if="!layoutState.isAuth" class="overlay" :class="{ show: !layoutState.showSidebar }" @click="toggleSidebar"></div>
                <div v-if="!layoutState.isAuth" class="search-overlay" :class="{ show: layoutState.showSearch }" @click="toggleSearch"></div>

                <Sidebar v-if="!layoutState.isAuth"></Sidebar>

                <button
                    v-if="!layoutState.isAuth"
                    type="button"
                    class="sidebar-edge-toggle"
                    :class="{ 'sidebar-edge-toggle--closed': !layoutState.isSidebarVisuallyOpen }"
                    :title="layoutState.isSidebarVisuallyOpen ? 'Recolher menu' : 'Abrir menu'"
                    :aria-label="layoutState.isSidebarVisuallyOpen ? 'Recolher menu' : 'Abrir menu'"
                    @click="toggleSidebar"
                >
                    <svg
                        v-if="layoutState.isSidebarVisuallyOpen"
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2.25"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        aria-hidden="true"
                    >
                        <polyline points="15 18 9 12 15 6"></polyline>
                    </svg>
                    <svg
                        v-else
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2.25"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        aria-hidden="true"
                    >
                        <polyline points="9 18 15 12 9 6"></polyline>
                    </svg>
                </button>

                <div id="content" class="main-content" :class="layoutState.isAuth ? 'auth-layout' : ''">
                    <div
                        v-if="!layoutState.isAuth"
                        class="main-content-toolbar"
                    >
                        <div
                            id="breadcrumb"
                            class="vue-portal-target main-content-toolbar__breadcrumb d-flex flex-grow-1 align-items-center min-w-0"
                        ></div>
                    </div>
                    <router-view v-if="routeContentReady" />
                </div>

                <app-settings v-if="!layoutState.isAuth" />
                <NotificationToast />
                <LegalGraceBanner />
                <TryNewDashboardModal v-if="!layoutState.isAuth" />
            </div>
        </template>
    </div>
</template>

<script setup>
    import { computed, nextTick, onMounted, ref, watch } from 'vue';
    import { useRoute } from 'vue-router';
    import { useLayout } from '@/composables/useLayout';
    import { useUserDashboardPreferencesStore } from '@/stores/userDashboardPreferences';
    import { shouldUseDashboardV2Shell } from '@/utils/dashboardV2Shell';
    import Header from "../components/layout/header.vue";
    import Sidebar from "../components/layout/sidebar.vue";
    import appSettings from "../components/app-settings.vue";
    import NotificationToast from "../components/notifications/NotificationToast.vue";
    import LegalGraceBanner from "../components/legal/LegalGraceBanner.vue";
    import DashboardV2AppShell from "./DashboardV2AppShell.vue";
    import TryNewDashboardModal from "@/components/dashboard-v2/TryNewDashboardModal.vue";

    const route = useRoute();
    const { layoutState, toggleSidebar, toggleSearch } = useLayout();
    const dashboardPrefs = useUserDashboardPreferencesStore();

    const desiredV2Shell = computed(
        () =>
            !layoutState.value.isAuth &&
            shouldUseDashboardV2Shell(dashboardPrefs.isNewDashboardActive, route)
    );

    /**
     * Troca de shell desmonta #breadcrumb. Se a página ainda tiver Teleport ativo,
     * o Vue quebra com "Cannot read properties of null (reading 'emitsOptions')".
     * Sequência: desmontar rota → trocar shell → remountar rota.
     */
    const activeShellIsV2 = ref(desiredV2Shell.value);
    const routeContentReady = ref(true);
    let shellSwapToken = 0;

    watch(desiredV2Shell, async (next) => {
        if (next === activeShellIsV2.value) return;

        const token = ++shellSwapToken;
        routeContentReady.value = false;
        await nextTick();
        if (token !== shellSwapToken) return;

        activeShellIsV2.value = next;
        await nextTick();
        if (token !== shellSwapToken) return;

        routeContentReady.value = true;
    });

    const containerClasses = computed(() => [
        !layoutState.value.showSidebar ? 'sidebar-closed sbar-open' : '',
        layoutState.value.isSidebarVisuallyOpen ? 'sidebar-visually-open' : 'sidebar-visually-closed',
        layoutState.value.sidebarSkipTransition ? 'sidebar-instant' : '',
        layoutState.value.menuStyle === 'collapsible-vertical' ? 'collapsible-vertical-mobile' : '',
        layoutState.value.isAuth ? 'main-container-auth' : ''
    ]);

    onMounted(() => {
        dashboardPrefs.fetchPreferences().catch(() => {});
    });
</script>
